import {
  FileUploadContext,
  FileUploadDropzone,
  FileUploadHiddenInput,
  FileUploadItem,
  FileUploadItemDeleteTrigger,
  FileUploadItemGroup,
  FileUploadItemName,
  FileUploadItemPreview,
  FileUploadItemPreviewImage,
  FileUploadRoot,
  type FileUploadRootProps,
} from "@ark-ui/react";
import { useTranslation } from "react-i18next";
import { LuTrash } from "react-icons/lu";

import { css, cx, sva } from "../../styled-system/css";

export const FileUploadStyle = sva({
  slots: [
    "dropzone",
    "item",
    "itemDeleteTrigger",
    "itemGroup",
    "itemName",
    "itemPreview",
    "itemPreviewIcon",
    "label",
    "root",
  ],

  base: {
    dropzone: {
      alignItems: "center",
      borderColor: "border",
      borderRadius: "6px",
      borderStyle: "dashed",
      borderWidth: "1px",
      cursor: "pointer",
      display: "flex",
      flexDirection: "column",
      fontSize: "14px",
      gap: "8px",
      lineHeight: 1,
      padding: "24px",
      transition: "all 0.2s",
      width: "stretch",
      _hover: { backgroundColor: "#00000008" },
      _disabled: { cursor: "not-allowed", opacity: 0.6 },
    },
    item: {
      alignItems: "center",
      display: "flex",
      gap: "12px",
      borderColor: "border",
      borderRadius: "6px",
      borderStyle: "solid",
      borderWidth: "1px",
      padding: "8px 16px",
      width: "stretch",
    },
    itemDeleteTrigger: {
      alignItems: "center",
      aspectRatio: "1 / 1",
      cursor: "pointer",
      backgroundColor: "#F4F4F5",
      borderRadius: "6px",
      color: "#18181B",
      display: "inline-flex",
      fontSize: "14px",
      fontWeight: 500,
      justifyContent: "center",
      height: "40px",
      lineHeight: 1,
      transition: "all 0.2s ease-in-out",
      _hover: {
        opacity: 0.6,
      },
    },
    itemGroup: {
      display: "flex",
      flexDirection: "column",
      gap: "8px",
      width: "stretch",
    },
    itemName: {
      flexGrow: 1,
      minWidth: 0,
      fontSize: "14px",
      overflow: "clip",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      lineHeight: 1,
    },
    itemPreviewIcon: { height: "20px", width: "auto" },
    root: {
      alignItems: "flex-start",
      display: "flex",
      flexDirection: "column",
      gap: "8px",
    },
  },
});

const FILE_UPLOAD_STYLE = css.raw({
  '& [data-scope="file-upload"][data-part="dropzone"]': {
    backgroundColor: "white",
    cursor: "pointer",
    borderColor: "#E4E4E7",
    borderRadius: "6px",
    borderStyle: "dashed",
    borderWidth: "1px",
    padding: "23px",
    transition: "filter 0.1s ease-in-out, transform 0.1s ease-in",
    width: "stretch",
    _active: { filter: "brightness(0.9)", transform: "scale(0.975)" },
    _disabled: { cursor: "not-allowed", opacity: 0.5 },
    _focus: { filter: "brightness(0.975)" },
    _hover: { filter: "brightness(0.95)" },
  },

  '& [data-scope="file-upload"][data-part="dropzone-text"]': {
    fontFamily: '"Noto Sans JP", "Noto Color Emoji", serif',
    fontSize: "0.875rem",
    lineHeight: 10 / 7,
    textAlign: "center",
  },

  '& [data-scope="file-upload"][data-part="dropzone-text"]:not(:first-child)': { marginTop: "4px" },

  '& [data-scope="file-upload"][data-part="item"]': {
    alignItems: "center",
    display: "flex",
    gap: "8px",
    borderColor: "#E4E4E7",
    borderRadius: "6px",
    borderStyle: "solid",
    borderWidth: "1px",
    padding: "8px 16px",
    marginTop: "8px",
    width: "stretch",
    _disabled: { opacity: 0.5 },
  },

  '& [data-scope="file-upload"][data-part="item-delete-trigger"]': {
    cursor: "pointer",
    backgroundColor: "#F4F4F5",
    borderRadius: "6px",
    color: "#18181B",
    padding: "12px",
    transition: "filter 0.1s ease-in-out, transform 0.1s ease-in",
    _active: { filter: "brightness(0.9)", transform: "scale(0.975)" },
    _disabled: { cursor: "not-allowed" },
    _focus: { filter: "brightness(0.975)" },
    _hover: { filter: "brightness(0.95)" },
  },

  '& [data-scope="file-upload"][data-part="item-group"]': { width: "stretch" },

  '& [data-scope="file-upload"][data-part="item-name"]': {
    flexGrow: 1,
    fontFamily: '"Noto Sans JP", "Noto Color Emoji", serif',
    fontSize: "0.875rem",
    lineBreak: "anywhere",
    lineHeight: 10 / 7,
    minWidth: 0,
  },
});

export function FileUpload({ className, ...props }: FileUploadRootProps) {
  const { t } = useTranslation();

  return (
    <FileUploadRoot {...props} className={cx(css(FILE_UPLOAD_STYLE), className)}>
      <FileUploadDropzone>
        <p data-part="dropzone-text" data-scope="file-upload">
          {t("%DragAndDrop.0%")}
        </p>
        <p data-part="dropzone-text" data-scope="file-upload">
          {t("%Or.0%")}
        </p>
        <p data-part="dropzone-text" data-scope="file-upload">
          {t("%Select.1%")}
        </p>
      </FileUploadDropzone>
      <FileUploadItemGroup>
        <FileUploadContext>
          {function ({ acceptedFiles }) {
            return acceptedFiles.map(function (file, index) {
              return (
                <FileUploadItem file={file} key={index}>
                  <FileUploadItemPreview type="image/*">
                    <FileUploadItemPreviewImage />
                  </FileUploadItemPreview>
                  <FileUploadItemName />
                  <FileUploadItemDeleteTrigger>
                    <LuTrash />
                  </FileUploadItemDeleteTrigger>
                </FileUploadItem>
              );
            });
          }}
        </FileUploadContext>
      </FileUploadItemGroup>
      <FileUploadHiddenInput />
    </FileUploadRoot>
  );
}
